import { Box, Stack, styled, Typography } from '@mui/material'
import type { BoxProps } from '@mui/material'
import { Fragment, useMemo } from 'react'
import type { FC, ReactElement } from 'react'
import { useTranslation } from 'react-i18next'

import type { HoldingWeekDto } from '@hcr/api/consumer'
import { COLORS, unit } from '@hcr/ui'

import { getYearHeadingId, YearHeading } from '../common/year-heading'
import { prepareWeekPreviewsData } from '../utils'

import { WeekPreview } from './week-preview'

interface NonVillaWeeksPreviewProps {
  holdingId: string
  weeks: HoldingWeekDto[]
}

export const NonVillaWeeksPreview: FC<NonVillaWeeksPreviewProps> = ({ weeks, holdingId }) => {
  const { t } = useTranslation()
  const weekPreviewsData = useMemo(() => prepareWeekPreviewsData(weeks ?? []), [weeks])
  const yearGroups = Array.from(weekPreviewsData.entries())

  return (
    <Stack
      bgcolor={COLORS.vaalea['60%']}
      width='100%'
      paddingX={unit(1.75)}
      paddingBottom={unit(1.75)}
      marginBottom={unit(1.75)}
    >
      <Typography
        variant='uppercaseS'
        paddingTop={unit(5)}
        paddingRight={unit(6)}
        paddingBottom={unit(2)}
        paddingLeft={unit(3.25)}
      >
        {t('owner-holding.your-vacation-week-details')}
      </Typography>
      {yearGroups.map(([year, yearWeeks], groupIndex) => (
        <Fragment key={year}>
          <YearHeading
            year={year}
            sx={{
              bgcolor: COLORS.white,
              paddingTop: unit(5),
              paddingRight: unit(6),
              paddingBottom: unit(1.5),
              paddingLeft: unit(3.25),
            }}
          />
          <Stack
            component='ul'
            aria-labelledby={getYearHeadingId(year)}
            bgcolor={COLORS.vaalea['60%']}
            direction='column'
            gap={unit(2)}
            marginBottom={groupIndex === yearGroups.length - 1 ? 0 : unit(1.75)}
          >
            {yearWeeks.map(week => (
              <WeekListItem key={week.week_start} component='li'>
                <WeekPreview holdingId={holdingId} week={week} />
              </WeekListItem>
            ))}
          </Stack>
        </Fragment>
      ))}
    </Stack>
  )
}

const WeekListItem = styled(Box)`
  display: block;
` as (props: BoxProps<'li'>) => ReactElement
